import { CacheService } from '~/services/cache/cache'
import type { Services, ServicesDeps } from './types'
import { SearchProcessorService } from '~/features/email-importer/services/search-processor'
import { classifyEmailAsInvoice } from '~common/invoice-processing/crude-classification'
import { FrontendInvoiceProcessorService } from '~/features/email-invoice-processing/services'
import { pdfToText } from '~/features/email-invoice-processing/pdf-to-text'
import { FirebaseAuthService } from '~common/google-auth/services/firebase-auth'
import { FirebaseLLMService } from '~/features/llms/service/firebase'
import { GoogleApiService } from '~/features/google-api/services/api'
import { FaviconService } from '~common/favicon-fetcher/service'
import { InvoiceSearchService } from './search'
import { InvoiceVendorMatchingService } from '~/features/invoice-vendor-matching/services'
import { EntitlementsService } from '~/features/entitlements/services'
import { ImporterService } from '~/features/email-importer/services/importer'
import { BankTransactionService } from './bank-transaction'
import { TransactionInvoiceMatchingService } from './transaction-invoice-matching'
import type { Invoice } from '~/storage/types'
import { ExtensionMessagingService } from '~/features/web-extension-messaging/services'

/**
 * All global events which trigger service class actions must be handled here
 */
export function setupServiceEventsListener(services: Services) {
    const unsubscribe = services.globalEvents.listen((event) => {
        if (event.type === 'updateImportState') {
            services.cache.setKey('importProgress', event.state)
        } else if (event.type === 'startImporter') {
            services.importer.start()
        }
    })

    return unsubscribe
}

export function createServices(deps: ServicesDeps): Services {
    const googleApi = new GoogleApiService({
        storageModules: deps.storage.modules,
        services: {
            functions: deps._functions,
        },
    })
    const llms = new FirebaseLLMService({ functions: deps._functions })

    const authService = new FirebaseAuthService({
        auth: deps.auth,
        config: deps.config.getConfig('firebaseAuth'),
        services: {
            _functions: deps._functions,
        },
    })

    const searchService = new InvoiceSearchService()

    const cacheService = new CacheService({
        statePersisters: deps.statePersisters,
        storage: deps.storage,
        getServices: () => ({
            auth: authService,
            googleApi,
            search: searchService,
            functions: deps._functions,
            globalEvents: deps.globalEvents,
        }),
    })

    const faviconService = new FaviconService({
        storage: deps.storage.modules.favicon,
        functions: deps._functions,
    })

    const invoiceVendorMatching = new InvoiceVendorMatchingService({
        getServices: () => ({
            errorTracker: deps.errorTracker,
            cache: cacheService,
        }),
        storageModules: deps.storage.modules,
    })

    const entitlementsService = new EntitlementsService({
        storageModules: {
            auth: deps.storage.modules.auth,
            entitlements: deps.storage.modules.entitlements,
        },
        functions: deps._functions,
        getServices: () => ({
            errorTracker: deps.errorTracker,
            cache: cacheService,
            globalEvents: deps.globalEvents,
            auth: authService,
        }),
    })

    const bankTransaction = new BankTransactionService(
        deps.storage.modules.bankTransaction,
        cacheService,
        () => ({ llms }),
    )

    const transactionInvoiceMatching = new TransactionInvoiceMatchingService({
        storage: deps.storage,
        getServices: () => ({
            cache: cacheService,
            errorTracker: deps.errorTracker,
            bankTransaction,
        }),
    })
    const invoiceProcessor = new FrontendInvoiceProcessorService({
        getServices: () => ({
            errorTracker: deps.errorTracker,
            googleApi,
            llms,
            cache: cacheService,
            favicon: faviconService,
            invoiceVendorMatching: invoiceVendorMatching,
            entitlements: entitlementsService,
            globalEvents: deps.globalEvents,
            cloudFunctions: deps.cloudFunctions,
            transactionInvoiceMatching,
        }),
        pdfFileToText: pdfToText,
        classifyEmailAsInvoice,
        storageModules: deps.storage.modules,
        localStorageModules: deps.storage.localModules,
        config: deps.config.getConfig('invoiceProcessor'),
        importerStatePersister: deps.statePersisters.importer,
        invoiceProcessingQueueStatePersister: deps.statePersisters.invoiceQueue,
    })

    const searchProcessorService = new SearchProcessorService({
        getServices: () => ({
            googleApi,
            invoiceProcessor,
            errorTracker: deps.errorTracker,
        }),
    })
    const importer = new ImporterService({
        getServices: () => ({
            invoiceProcessor,
            errorTracker: deps.errorTracker,
            globalEvents: deps.globalEvents,
        }),
        importStatePersister: deps.statePersisters.importer,
        processSearchPage:
            deps.importerPageProcessor ??
            searchProcessorService.searchAndScheduleProcessingEmails.bind(
                searchProcessorService,
            ),
    })

    // bankTransaction already created above

    const extensionMessaging = new ExtensionMessagingService({
        auth: authService,
        _functions: deps._functions,
        webExtensionApi: deps.webExtensionApi,
        extensionId: deps.webExtensionId,
    })

    extensionMessaging.connect()

    // Realtime listener for cloud-created invoices -> cache + trigger matching
    const startAtTs = Date.now()
    const unsubscribeInvoices =
        deps.storage.modules.emailInvoice.subscribeToInvoices(
            async ({ added, modified, removed }) => {
                try {
                    if (added.length > 0) {
                        await cacheService.setInvoice(added as Invoice[])
                        deps.globalEvents.emit({ type: 'invoiceAdded' })
                        for (const inv of added) {
                            try {
                                await transactionInvoiceMatching.findMatchesForInvoice(
                                    inv.id,
                                    false,
                                )
                            } catch (err) {
                                console.warn(
                                    'Matching failed for invoice',
                                    inv.id,
                                    err,
                                )
                            }
                        }
                    }
                    if (modified.length > 0) {
                        await cacheService.setInvoice(modified as Invoice[])
                    }
                    if (removed.length > 0) {
                        // Optional: implement removal in cache service if desired
                    }
                } catch (e) {
                    console.warn('Invoice realtime handler error', e)
                }
            },
            { startAtTimestamp: startAtTs },
        )

    return {
        importer,
        llms,
        googleApi,
        globalEvents: deps.globalEvents,
        invoiceVendorMatching,
        invoiceProcessor,
        auth: authService,
        cache: cacheService,
        config: deps.config,
        errorTracker: deps.errorTracker,
        favicon: faviconService,
        entitlements: entitlementsService,
        searchProcessor: searchProcessorService,
        search: searchService,
        cloudFunctions: deps.cloudFunctions,
        // TODO: remove this
        _functions: deps._functions,
        transactionInvoiceMatching,
        bankTransaction,
        extensionMessaging,
    }
}
